import {
  Table,
  Input,
  InputNumber,
  Popconfirm,
  Form,
  Button,
  Typography,
} from "antd";
import Loader from "components/common/Loader";
import {
  useFetchInventoryQuery,
  useUpdateInventoryMutation,
  useFetchInventoryFilterOptionsQuery,
} from "features/store";
import React, { useState } from "react";
import EditableTable from "./EditableTable";
import styled from "styled-components";
import { TableTitleStyles } from "./Inventory.style";
import moment from "moment";
import { getColorHex } from "../../utils/identifyColor";

// Helper function to extract color code from strings like "FX1 YELLOW" or "FX2 MAROON"
const extractColorCode = (colorString) => {
  if (!colorString || typeof colorString !== 'string') return '';
  
  // Extract the code part (letters and numbers before the first space)
  const match = colorString.match(/^([A-Z0-9]+)/i);
  return match ? match[1].toUpperCase() : '';
};

const FlexiStock = ({ item, pagination, onPaginationChange, onActivityLogClick, colorSearch }) => {
  const currentPage = pagination?.current || 1;
  const pageSize = pagination?.pageSize || 10;

  const [serverFilters, setServerFilters] = useState({
    color: null,
  });

  // When searching, fetch all data without pagination
  // Otherwise use normal pagination
  const shouldFetchAll = colorSearch && colorSearch.trim() !== "";

  const { data, isLoading } = useFetchInventoryQuery({
    cloth_type: item,
    ...(shouldFetchAll ? { no_pagination: true } : { page: currentPage, page_size: pageSize }),
    ...(serverFilters?.color ? { color: serverFilters.color } : {}),
  });

  // Fetch filter options from all data, not just paginated data
  const { data: filterOptions } = useFetchInventoryFilterOptionsQuery({
    cloth_type: item,
  });

  // Handle paginated response structure (Django REST Framework returns {count, results, next, previous})
  // or non-paginated array response (when no_pagination=true)
  let dataSource = Array.isArray(data) ? data : (data?.results || []);
  const totalCount = shouldFetchAll 
    ? dataSource.length 
    : (data?.count || (Array.isArray(data) ? data.length : 0));

  // Filter by color search if provided (case-insensitive partial match)
  if (colorSearch && colorSearch.trim() !== "") {
    const searchTerm = colorSearch.trim().toLowerCase();
    dataSource = dataSource.filter((item) => {
      const color = item?.color_cloth?.color || "";
      return color.toLowerCase().includes(searchTerm);
    });
  }

  // Use filter options from API (all data) instead of paginated data
  const uniqueColors = filterOptions?.colors?.length 
    ? filterOptions.colors.filter((it) => it !== null && it !== '')
    : [...new Set(dataSource?.map(item => item?.color_cloth?.color))].filter((it) => it !== null);
  const codeArry = filterOptions?.color_codes?.length
    ? filterOptions.color_codes.filter((it) => it !== null && it !== '')
    : [...new Set(dataSource?.map(item => item?.color_cloth?.color_code))].filter((it) => it !== null);

  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
    },
    {
      title: "Color",
      key: "color",
      dataIndex: ["color_cloth", "color"],
      sorter: (a, b) => {
        const codeA = extractColorCode(a?.color_cloth?.color || '');
        const codeB = extractColorCode(b?.color_cloth?.color || '');
        return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
      },
      defaultSortOrder: 'ascend',
      render: (text, record) => {
        const hexColor = getColorHex(record?.color_cloth || {});
        
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span>{text}</span>
            {hexColor ? (
              <div
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: hexColor,
                  border: hexColor.toLowerCase() === '#ffffff' || hexColor.toLowerCase() === '#fff' ? '1px solid #d9d9d9' : 'none',
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              />
            ) : (
              <div
                style={{
                  width: 24,
                  height: 24,
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #d9d9d9',
                  borderRadius: 4,
                  flexShrink: 0,
                }}
              />
            )}
          </div>
        );
      },
    },
    {
      title: "Last Updates",
      dataIndex: "last_updated_date",
      render: (text) => {
        if (text && moment(text).isValid()) {
          return moment(text).format('YYYY-MM-DD');
        }
        return text;
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: "15%",
      editable: true,
    }
  ];

  const inventory = "invetetetet";

  const handleTableChange = (_pagination, filters) => {
    // Only handle pagination changes, not filter changes since we removed filters
    if (_pagination && typeof onPaginationChange === "function" && !shouldFetchAll) {
      onPaginationChange(_pagination.current, _pagination.pageSize);
    }
  };

  return (
    <div className="inventory-table">
      <TableTitleStyles>{item}</TableTitleStyles>
      <EditableTable
        inventory={inventory}
        loading={isLoading}
        dataSource={dataSource}
        columns={columns}
        onTableChange={handleTableChange}
        onActivityLogClick={onActivityLogClick}
        pagination={shouldFetchAll ? false : {
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (page, size) => {
            onPaginationChange(page, size);
          },
          onShowSizeChange: (current, size) => {
            onPaginationChange(1, size);
          },
        }}
      />
    </div>
  );
};

export default FlexiStock;

const Style = styled.div`

`;
